import { Occupation, UpdateProfileStatus, useCustomerDetails, useUpdateEmploymentInfo } from '@egw/api'
import { Button, Field, ModalDrawer, RadioGroup, type RadioGroupOption, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Controller, useForm } from 'react-hook-form'
import { Logger } from '@egw/logger'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { useEffect, useState, type FC } from 'react'
import { EmploymentField } from '../../types'

const log = new Logger('Update Occupation')

interface EmploymentStatusFormInterface {
  [EmploymentField.OCCUPATION]: Occupation
}

export const EditOccupationField: FC = () => {
  const { t } = useTranslation('features-settings')
  const { isOpen, openModal, closeModal } = useModalState()
  const [updateError, setUpdateError] = useState<boolean>(false)

  const { data, refetch } = useCustomerDetails()

  const value = data?.customer.occupation

  const { mutate: updateEmploymentInfo, isLoading } = useUpdateEmploymentInfo({
    onSuccess: (res) => {
      if (res.__typename === 'UpdateProfileError' || res.status === UpdateProfileStatus.FAILED) {
        log.error('Error updating occupation field', { data })
        setUpdateError(true)
      } else if (res.status === UpdateProfileStatus.COMPLETE) {
        void refetch()
          .catch((error: unknown) => {
            log.error(error)
          })
          .finally(() => {
            setUpdateError(false)
            closeModal()
            reset()
          })
      }
    },
    onError: () => {
      setUpdateError(true)
    },
  })

  const defaultValues = {
    [EmploymentField.OCCUPATION]: value,
  }

  const { handleSubmit, control, reset, formState } = useForm<EmploymentStatusFormInterface>({
    mode: 'all',
    defaultValues,
  })

  useEffect(() => {
    if (data) {
      reset({
        [EmploymentField.OCCUPATION]: value,
      })
    }
  }, [data, reset, value])

  const onSubmit = async (formData: any) => {
    setUpdateError(false)
    log.debug('Submitting Form', { formData })
    void updateEmploymentInfo(formData)
  }

  return value ? (
    <>
      <Field
        valueClassName="font-light"
        className="w-full justify-between border-list mb-0 py-4 px-2"
        onEdit={openModal}
        value={t('employment_labels.occupation')}
        rightValue={t(`occupation.${value}`)}
      />
      <ModalDrawer title={t('employment_labels.occupation')} isOpen={isOpen} onClose={closeModal}>
        <form
          id="updateOccupationForm"
          className="flex flex-col gap-4 h-full md:h-screen justify-between"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit(onSubmit)()
          }}
        >
          <div className="flex flex-col gap-10">
            <div>
              <p className="mt-4 mb-4 font-semibold">{t('update_occupation.your_occupation')}</p>

              <Controller
                name={EmploymentField.OCCUPATION}
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    value={field.value}
                    options={Object.values(Occupation).map((item) => {
                      return {
                        label: t(`occupation.${item}`),
                        value: item,
                      }
                    })}
                  />
                )}
              />
            </div>
            {updateError ? (
              <div className="flex bg-alertErrorBg p-3 rounded-md">
                <InformationCircleIcon className="w-6 h-6 fill-red-500" />
                <p className="text-sm text-alertErrorText ml-2">{t('common.update_error')} </p>
              </div>
            ) : null}
          </div>
          <Button
            id="updateOccupation"
            type="submit"
            isLoading={isLoading}
            isDisabled={!formState.isValid}
            className="mt-4 md:mt-0"
          >
            {t('common.confirm')}
          </Button>
        </form>
      </ModalDrawer>
    </>
  ) : null
}
